import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import downIcon from "../../../assets/down-icon.svg";
import deleteIcon from "../../../assets/delete-red.svg";
import { ProcedureFlowServices } from "../../../services/ProcedureFlowServices";

/** Build display: "CODE - Name" when code exists, else name. */
function formatProcedureLabel(p) {
  if (p == null) return "";
  const name = String(p.name ?? "").trim();
  const code = String(p.procedureCode ?? "").trim();
  if (code && name) return `${code} - ${name}`;
  if (name) return name;
  return String(p.id ?? "");
}

/** Case-insensitive match on procedure id, code, or name (for dropdown search). */
function procedureSearchMatches(p, rawTerm) {
  const q = String(rawTerm ?? "").trim().toLowerCase();
  if (!q) return true;
  const id = String(p?.procedureId ?? "").toLowerCase();
  const code = String(p?.procedureCode ?? "").trim().toLowerCase();
  const name = String(p?.name ?? "").toLowerCase();
  const combined = `${code} ${name}`.trim().toLowerCase();
  return (
    id.includes(q) ||
    code.includes(q) ||
    name.includes(q) ||
    combined.includes(q)
  );
}

function pickProcedureCodeFromMaster(pm) {
  if (!pm || typeof pm !== "object") return "";
  const c =
    pm.procedureCode ??
    pm.pmjayCode ??
    pm.code ??
    pm.hcpcsCode ??
    pm.icdCode ??
    "";
  return String(c).trim();
}

export default function ProcedureMappingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const prefillAppliedRef = useRef(false);

  const [mappedChildList, setMappedChildList] = useState([]);
  const [mappedChildListLoading, setMappedChildListLoading] = useState(false);

  const [schemes, setSchemes] = useState([]);
  const [schemesLoading, setSchemesLoading] = useState(false);
  const [schemePage, setSchemePage] = useState(1);
  const [schemeHasMore, setSchemeHasMore] = useState(true);
  const [schemeLoadMore, setSchemeLoadMore] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState(null);

  const [procedures, setProcedures] = useState([]);
  const [selectedProcedure, setSelectedProcedure] = useState(null);
  const [procedureLoading, setProcedureLoading] = useState(false);
  const [procedurePage, setProcedurePage] = useState(1);
  const [procedureHasMore, setProcedureHasMore] = useState(true);
  const [procedureLoadMore, setProcedureLoadMore] = useState(false);

  const [selectedMappedProcedures, setSelectedMappedProcedures] = useState([]);
  const [mappingLoading, setMappingLoading] = useState(false);

  const PAGE_SIZE = 200;

  /**
   * Fetch child (mapped) procedures for the selected parent procedure.
   * GET child-procedure-list?procedureSchemeId=<parent id>. No pagination â€“ request high limit to get all.
   */
  const fetchChildProcedureList = async (procedureSchemeId) => {
    if (!procedureSchemeId) {
      setMappedChildList([]);
      return;
    }
    setMappedChildListLoading(true);
    try {
      const response = await ProcedureFlowServices.getChildProcedureList({
        procedureSchemeId,
        limit: 500,
      });
      const raw = response?.data?.mappedProcdures ?? response?.data?.mappedProcedures ?? [];
      const list = raw.map((item) => ({
        id: item.id,
        childProcedureSchemeId: item.childProcedureSchemeId,
        procedureName:
          item.childschemeprocedure?.proceduremaster?.procedure ??
          item.childSchemeProcedure?.proceduremaster?.procedure ??
          "",
      }));
      setMappedChildList(list);
    } catch (err) {
      console.error("Failed to fetch child procedure list", err);
      setMappedChildList([]);
    } finally {
      setMappedChildListLoading(false);
    }
  };

  const [deletingMappingId, setDeletingMappingId] = useState(null);

  /**
   * Delete (set INACTIVE) a mapped child via add-update-procedure-procedure-mapping.
   * Payload: [{ id: "<mapping-id from child-procedure-list>", status: "INACTIVE" }].
   */
  const handleDeleteMappedChild = async (item) => {
    const mappingId = item?.id;
    if (!mappingId) return;
    if (!selectedProcedure?.id) {
      alert("Please select a parent procedure first.");
      return;
    }
    setDeletingMappingId(mappingId);
    try {
      await ProcedureFlowServices.addUpdateProcedureProcedureMapping([
        { id: String(mappingId), status: "INACTIVE" },
      ]);
      setMappedChildList((prev) => prev.filter((m) => m.id !== mappingId));
      alert("Mapping removed successfully.");
    } catch (error) {
      console.error("Failed to remove procedure mapping", error);
      alert(error?.response?.data?.message || "Failed to remove mapping. Please try again.");
    } finally {
      setDeletingMappingId(null);
    }
  };

  /**
   * Fetch procedures for the selected scheme only.
   * Uses scheme-procedure-list?schemeId=<id>. Each item's top-level "id" is the procedure-scheme record id used for mapping API.
   */
  const fetchProcedures = async (schemeId, page = 1, append = false) => {
    if (!schemeId) return;
    try {
      if (page === 1) setProcedureLoading(true);
      else setProcedureLoadMore(true);
      const response = await ProcedureFlowServices.getSchemeProcedureList({
        schemeId,
        page,
        limit: PAGE_SIZE,
        procedureSchemeData: "ACTIVE",
      });
      const rawList = response?.data?.list ?? [];
      if (rawList.length > 0 && page === 1) {
        console.log("Scheme procedure list â€“ first item (id = procedure-scheme record id for mapping):", rawList[0]);
      }
      const list = rawList
        .map((item) => {
          const pm = item.proceduremaster ?? item.procedureMaster ?? {};
          const name = pm.procedure ?? item.procedure ?? "";
          const procedureId = item.procedureId ?? pm.procedureId ?? pm.id;
          const procedureCode = pickProcedureCodeFromMaster(pm);
          return {
            id: String(item.id ?? ""),
            procedureId,
            name,
            procedureCode,
          };
        })
        .filter((item) => item.id && item.name);
      const seen = new Set();
      const deduped = list.filter((item) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
      if (append) {
        setProcedures((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newItems = deduped.filter((p) => !existingIds.has(p.id));
          return [...prev, ...newItems];
        });
      } else {
        setProcedures(deduped);
      }
      setProcedureHasMore(rawList.length >= PAGE_SIZE);
      setProcedurePage(page + 1);
    } catch (error) {
      console.error("Failed to fetch procedures for scheme", error);
      setProcedures((prev) => (append ? prev : []));
      setProcedureHasMore(false);
    } finally {
      setProcedureLoading(false);
      setProcedureLoadMore(false);
    }
  };

  const fetchSchemes = async (page = 1, append = false) => {
    try {
      if (page === 1 && !append) setSchemesLoading(true);
      if (append) setSchemeLoadMore(true);
      const response = await ProcedureFlowServices.getDefaultSchemeTypes({
        page,
        limit: PAGE_SIZE,
      });
      const rawList = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      if (append) {
        setSchemes((prev) => {
          const existingIds = new Set(prev.map((s) => s.id));
          const newItems = rawList.filter((s) => !existingIds.has(s.id));
          return [...prev, ...newItems];
        });
      } else {
        setSchemes(rawList);
      }
      setSchemeHasMore(rawList.length >= PAGE_SIZE);
      setSchemePage(page + 1);
    } catch (err) {
      console.error("Failed to fetch scheme types", err);
      setSchemes((prev) => (append ? prev : []));
      setSchemeHasMore(false);
    } finally {
      setSchemesLoading(false);
      if (append) setSchemeLoadMore(false);
    }
  };

  // const fetchProcedureList = async (searchKey = "") => {
  //   try {
  //     setProcedureListLoading(true);
  //     const response = await ProcedureFlowServices.getProcedureslist(searchKey);
  //     console.log("fetchProcedureList API response:", response?.data);
  //     const list = Array.isArray(response?.data?.list)
  //       ? response.data.list
  //           .map((item) => ({
  //             id: item.id,
  //             procedureId: item.procedureId,
  //             procedure: item.procedure,
  //             compositKey: item.compositKey,
  //             name: item.procedure || item.compositKey || "",
  //           }))
  //           .filter((item) => item.id && item.name)
  //       : [];
  //     setProcedureList(list);
  //   } catch (error) {
  //     console.error("Failed to fetch procedure list", error);
  //     setProcedureList([]);
  //   } finally {
  //     setProcedureListLoading(false);
  //   }
  // };

  const handleMapProcedure = async () => {
    if (
      !selectedScheme ||
      !selectedProcedure ||
      !selectedMappedProcedures.length
    ) {
      alert("Please select scheme, procedure and mapped procedures");
      return;
    }

    // Mapping uses procedure-scheme record ids from scheme-procedure-list (each item's top-level "id")
    const parentProcedureSchemeId = String(selectedProcedure.id ?? "");
    const payload = selectedMappedProcedures.map((child) => ({
      parentProcedureSchemeId,
      childProcedureSchemeId: String(child.id ?? ""),
    }));

    console.log("Parent procedure (id = procedure-scheme record id):", selectedProcedure);
    console.log("Child procedures (selected mapped procedures):", selectedMappedProcedures);
    selectedMappedProcedures.forEach((child, index) => {
      console.log(`Child procedure [${index}]:`, child);
    });
    console.log("Procedureâ€“procedure mapping payload:", { parentProcedureSchemeId, payload });

    setMappingLoading(true);
    try {
      const response = await ProcedureFlowServices.addUpdateProcedureProcedureMapping(payload);
      const result = response?.data;
      if (Array.isArray(result) && result.length > 0) {
        console.log("Procedureâ€“procedure mapping saved:", result);
        alert(`Mapping saved: ${result.length} procedure(s) mapped.`);
      } else {
        alert("Mapping saved.");
      }
      fetchChildProcedureList(selectedProcedure.id);
    } catch (error) {
      console.error("Mapping failed", error);
      alert(error?.response?.data?.message || "Failed to save procedure mapping. Please try again.");
    } finally {
      setMappingLoading(false);
    }
  };

  const handleToggleMappedProcedure = (item) => {
    setSelectedMappedProcedures((prev) => {
      const exists = prev.some((selected) => selected.id === item.id);
      if (exists) {
        return prev.filter((selected) => selected.id !== item.id);
      }
      return [...prev, item];
    });
  };

  useEffect(() => {
    fetchSchemes(1, false);
  }, []);

  // Pre-select scheme when navigated from row click (has schemeId in state)
  useEffect(() => {
    const { schemeId, schemeName } = location.state || {};
    if (prefillAppliedRef.current || !schemeId || schemes.length === 0) return;
    const match = schemes.find((s) => String(s.id) === String(schemeId) || s.name === schemeName);
    if (match) {
      setSelectedScheme(match);
    }
  }, [schemes, location.state]);

  useEffect(() => {
    setProcedures([]);
    setSelectedProcedure(null);
    setSelectedMappedProcedures([]);
    setProcedurePage(1);
    setProcedureHasMore(true);
    if (selectedScheme?.id) {
      fetchProcedures(selectedScheme.id, 1, false);
    }
  }, [selectedScheme?.id]);

  // Pre-select procedure when navigated from row click (has procedureSchemeMasterId in state)
  useEffect(() => {
    const { procedureSchemeMasterId } = location.state || {};
    if (prefillAppliedRef.current || !procedureSchemeMasterId || !selectedScheme || procedures.length === 0) return;
    const match = procedures.find((p) => String(p.id) === String(procedureSchemeMasterId));
    if (match) {
      setSelectedProcedure(match);
      prefillAppliedRef.current = true;
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [selectedScheme, procedures, location.state, location.pathname, navigate]);

  useEffect(() => {
    if (selectedProcedure?.id) {
      fetchChildProcedureList(selectedProcedure.id);
    } else {
      setMappedChildList([]);
    }
  }, [selectedProcedure?.id]);

  const mappedProcedureSummary =
    selectedMappedProcedures.length > 0
      ? selectedMappedProcedures
          .map((item) => item.name)
          .filter(Boolean)
          .join(", ")
      : "";
  return (
    <div className="w-full py-12 px-4 bg-[#F3FAF7]">
      <div className=" rounded-2xl p-8">
        {/* ===== TITLE ===== */}
        <h1 className="font-lufga font-normal text-[40px] tracking-[-0.04em] text-black mb-6">
          Procedure - Procedure Mapping
        </h1>

        {/* ===== DROPDOWNS ===== */}
        <div className="flex flex-col gap-5 mb-8">
          <Dropdown
            placeholder={
              schemesLoading ? "Loading schemes..." : "Select Scheme"
            }
            options={schemes}
            labelKey="name"
            value={selectedScheme?.name || ""}
            onSelect={(scheme) => {
              setSelectedScheme(scheme);
            }}
            onOpen={() => {
              if (schemes.length === 0 && !schemesLoading) fetchSchemes(1, false);
            }}
            onScrollToBottom={() => {
              if (schemeHasMore && !schemeLoadMore) fetchSchemes(schemePage, true);
            }}
            hasMore={schemeHasMore}
            loadMoreLoading={schemeLoadMore}
          />

          <Dropdown
            placeholder={
              !selectedScheme
                ? "Select scheme first"
                : procedureLoading
                  ? "Loading procedures..."
                  : "Select Procedure"
            }
            options={procedures}
            labelKey="name"
            value={
              selectedProcedure ? formatProcedureLabel(selectedProcedure) : ""
            }
            formatOptionLabel={formatProcedureLabel}
            searchMatches={procedureSearchMatches}
            onSelect={(procedure) => setSelectedProcedure(procedure)}
            allowSearch
            searchPlaceholder="Search by code, name or procedure ID..."
            onSearch={() => {
              if (selectedScheme?.id && procedures.length === 0 && !procedureLoading) {
                fetchProcedures(selectedScheme.id, 1, false);
              }
            }}
            onOpen={() => {
              if (selectedScheme?.id && procedures.length === 0 && !procedureLoading) {
                fetchProcedures(selectedScheme.id, 1, false);
              }
            }}
            onScrollToBottom={() => {
              if (selectedScheme?.id && procedureHasMore && !procedureLoadMore) {
                fetchProcedures(selectedScheme.id, procedurePage, true);
              }
            }}
            hasMore={procedureHasMore}
            loadMoreLoading={procedureLoadMore}
            disabled={!selectedScheme}
          />
        </div>

        {/* ===== MAPPED PROCEDURES HEADER ===== */}
        <div className="flex flex-wrap items-start justify-between mb-4 gap-4">
          <h2 className=" font-lufga font-medium text-[32px] leading-[100%] tracking-[-0.04em] text-center">
            Mapped Procedures
          </h2>
          <div className="flex flex-col gap-2 w-full max-w-[520px]">
            <Dropdown
              placeholder={
                !selectedScheme
                  ? "Select scheme first"
                  : procedureLoading
                    ? "Loading..."
                    : "Select Mapped Procedure"
              }
              options={procedures}
              labelKey="name"
              formatOptionLabel={formatProcedureLabel}
              searchMatches={procedureSearchMatches}
              multiple
              selectedValues={selectedMappedProcedures}
              onSelectMultiple={handleToggleMappedProcedure}
              allowSearch
              searchPlaceholder="Search by code, name or procedure ID..."
              onSearch={() => {
                if (selectedScheme?.id && procedures.length === 0 && !procedureLoading) {
                  fetchProcedures(selectedScheme.id, 1, false);
                }
              }}
              onOpen={() => {
                if (selectedScheme?.id && procedures.length === 0 && !procedureLoading) {
                  fetchProcedures(selectedScheme.id, 1, false);
                }
              }}
              onScrollToBottom={() => {
                if (selectedScheme?.id && procedureHasMore && !procedureLoadMore) {
                  fetchProcedures(selectedScheme.id, procedurePage, true);
                }
              }}
              hasMore={procedureHasMore}
              loadMoreLoading={procedureLoadMore}
              disabled={!selectedScheme}
              className="w-full"
            />

            {selectedMappedProcedures.length > 0 && (
              <div className="border border-gray-200 rounded-2xl bg-white px-4 py-3">
                <div className="text-xs font-medium text-gray-500 mb-2">
                  Selected ({selectedMappedProcedures.length})
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedMappedProcedures.map((item) => (
                    <Chip
                      key={item.id}
                      label={formatProcedureLabel(item)}
                      onRemove={() => handleToggleMappedProcedure(item)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white p-5 border rounded-[15px]">
          {/* ===== LIST HEADER ===== */}
          <div className="font-lufga font-semibold text-[14px] leading-[155%] tracking-[0] mb-2">
            Procedure Name
          </div>

          {/* ===== LIST (no pagination â€“ all data on scroll) ===== */}
          <div className="border-t max-h-[400px] overflow-y-auto">
            {!selectedProcedure ? (
              <div className="py-8 px-2 text-sm text-gray-500 text-center">
                Select a parent procedure to view mapped procedures
              </div>
            ) : mappedChildListLoading ? (
              <div className="py-8 px-2 text-sm text-gray-500 text-center">
                Loading mapped procedures...
              </div>
            ) : mappedChildList.length === 0 ? (
              <div className="py-8 px-2 text-sm text-gray-500 text-center">
                No mapped procedures yet. Select procedures above and click Map Procedure.
              </div>
            ) : (
              mappedChildList.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-center justify-between py-4 px-2 ${
                    index % 2 === 1 ? "bg-gray-50" : "bg-white"
                  }`}
                >
                  <p className="text-sm text-gray-800 max-w-[90%] break-words">
                    {item.procedureName || "â€”"}
                  </p>
                  <img
                    src={deleteIcon}
                    alt="delete"
                    className="w-5 h-5 cursor-pointer flex-shrink-0 disabled:opacity-50"
                    onClick={() => handleDeleteMappedChild(item)}
                    style={{ pointerEvents: deletingMappingId === item.id ? "none" : undefined }}
                    title="Remove mapping"
                  />
                </div>
              ))
            )}
          </div>
        </div>

        {/* ===== CTA ===== */}
        <div className="flex justify-center mt-10">
          <button
            className="
    w-[380px]
    h-[88px]
    px-[20px]
    py-[20px]
    rounded-[60px]
    border-2
    border-[#5AC69F]
    bg-[#6ED1A7]
    text-black
    font-lufga
    font-medium
    text-[16px]
    leading-[155%]
    tracking-[0]
    flex
    items-center
    justify-center
    gap-[20px]
    hover:bg-[#5AC69F]
    transition
    disabled:opacity-70
    disabled:cursor-not-allowed
  "
            type="button"
            onClick={handleMapProcedure}
            disabled={mappingLoading}
          >
            {mappingLoading ? (
              <>
                <span className="inline-block w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                Mapping...
              </>
            ) : (
              "Map Procedure"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function Dropdown({
  placeholder,
  options = [],
  value = "",
  onSelect,
  labelKey,
  disabled = false,
  onOpen,
  multiple = false,
  selectedValues = [],
  onSelectMultiple,
  allowSearch = false,
  onSearch,
  searchPlaceholder = "Search procedure...",
  className = "",
  onScrollToBottom,
  hasMore = false,
  loadMoreLoading = false,
  /** Optional: full row label (e.g. "CODE - Name"). Falls back to renderLabel. */
  formatOptionLabel,
  /** Optional: (item, searchTerm) => boolean when allowSearch */
  searchMatches,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const menuRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open && searchTerm !== "") {
      setSearchTerm("");
    }
  }, [open, searchTerm]);

  const renderLabel = (item) => {
    if (item == null) return "";
    if (labelKey && item[labelKey]) return item[labelKey];
    if (typeof item === "string") return item;
    if (item.name) return item.name;
    if (item.procedure) return item.procedure;
    return item.id ?? "";
  };

  const getOptionLabel = (item) =>
    typeof formatOptionLabel === "function"
      ? formatOptionLabel(item)
      : renderLabel(item);

  const filteredOptions =
    allowSearch && searchTerm.trim()
      ? options.filter((opt) => {
          if (typeof searchMatches === "function") {
            return searchMatches(opt, searchTerm);
          }
          const label = getOptionLabel(opt).toLowerCase();
          return label.includes(searchTerm.trim().toLowerCase());
        })
      : options;

  const handleMenuScroll = () => {
    const el = menuRef.current;
    if (!el || !onScrollToBottom || !hasMore || loadMoreLoading) return;
    const { scrollTop, clientHeight, scrollHeight } = el;
    if (scrollTop + clientHeight >= scrollHeight - 80) {
      onScrollToBottom();
    }
  };

  const selectedSummary = multiple
    ? selectedValues.map((item) => getOptionLabel(item)).filter(Boolean)
    : [];

  const displayValue = multiple
    ? selectedSummary.length > 0
      ? selectedSummary.length > 2
        ? `${selectedSummary.slice(0, 2).join(", ")} +${
            selectedSummary.length - 2
          }`
        : selectedSummary.join(", ")
      : ""
    : value;

  return (
    <div
      ref={ref}
      className={`relative px-6 py-5 border rounded-full bg-white flex items-center justify-between ${
        disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
      } ${className}`}
      onClick={() => {
        if (disabled) return;
        if (!open) {
          onOpen && onOpen();
        }
        setOpen((prev) => !prev);
      }}
    >
      <span
        className={`text-sm ${
          displayValue ? "text-gray-800" : "text-gray-400"
        }`}
      >
        {displayValue || placeholder}
      </span>
      <img
        src={downIcon}
        className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
        alt="down-icon"
      />

      {open && !disabled && (
        <div
          ref={menuRef}
          onScroll={handleMenuScroll}
          className="absolute left-0 right-0 top-[calc(100%+12px)] bg-white rounded-2xl shadow-lg border border-gray-100 max-h-[320px] overflow-y-auto z-30"
        >
          {allowSearch && (
            <div className="p-3 border-b border-gray-100 sticky top-0 bg-white">
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setSearchTerm(nextValue);
                  onSearch && onSearch(nextValue);
                }}
                onClick={(event) => event.stopPropagation()}
                className="w-full border border-gray-200 rounded-full px-4 py-2 text-sm outline-none focus:border-[#5AC69F]"
                placeholder={searchPlaceholder}
              />
            </div>
          )}
          {options.length === 0 ? (
            <div className="px-6 py-3 text-sm text-gray-400">
              No options available
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-6 py-3 text-sm text-gray-400">
              No matching procedures
            </div>
          ) : (
            <>
              {filteredOptions.map((opt, index) => {
                const label = getOptionLabel(opt);
                const key = opt?.id ?? `${label}-${index}`;
                const isSelected =
                  multiple &&
                  selectedValues.some((selected) => selected?.id === opt?.id);
                return (
                  <div
                    key={key}
                    className={`px-6 py-3 text-sm hover:bg-[#F4FBF8] flex items-center justify-between gap-3 ${
                      isSelected ? "bg-[#F4FBF8] font-medium" : ""
                    }`}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (multiple) {
                        typeof onSelectMultiple === "function" &&
                          onSelectMultiple(opt);
                      } else {
                        typeof onSelect === "function" && onSelect(opt);
                        setOpen(false);
                      }
                    }}
                  >
                    <span className="break-words text-left flex-1 min-w-0 leading-snug">
                      {label}
                    </span>
                    {multiple && (
                      <span
                        className={`text-[#5AC69F] text-lg transition-opacity ${
                          isSelected ? "opacity-100" : "opacity-0"
                        }`}
                      >
                        ✓
                      </span>
                    )}
                  </div>
                );
              })}
              {loadMoreLoading && (
                <div className="px-6 py-3 text-sm text-gray-500 text-center border-t border-gray-100">
                  Loading...
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ===== Chip Component ===== */
function Chip({ label, onRemove }) {
  return (
    <div className="inline-flex items-start gap-2 px-4 py-2 bg-gray-100 rounded-2xl text-sm max-w-full min-w-0">
      <span className="break-words text-left flex-1 min-w-0">{label}</span>
      {onRemove ? (
        <span
          className="cursor-pointer flex-shrink-0 text-gray-600 hover:text-black"
        onClick={(event) => {
          event.stopPropagation();
          onRemove();
        }}
      >
        ×
      </span>
      ) : (
        <span className="cursor-pointer flex-shrink-0">×</span>
      )}
    </div>
  );
}